import { Menu, Transition } from '@headlessui/react'
import {
    ArrowRightOnRectangleIcon,
    ChevronDownIcon,
    ClipboardDocumentCheckIcon,
    Cog6ToothIcon,
    HomeIcon,
    KeyIcon,
    UserGroupIcon,
    UsersIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'

import { cn } from '@/utils/cn'

import logo from '../../logo-black-no-bg.svg'

import { useProviderSession } from './ProviderSession'

const PROVIDER_NAV = [
    { text: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { text: 'Team Members', href: '/team', icon: UserGroupIcon, adminOnly: true },
    { text: 'Clients', href: '/clients', icon: UsersIcon },
    { text: 'Roadmap Reviews', href: '/roadmap-reviews', icon: ClipboardDocumentCheckIcon },
    { text: 'Licenses', href: '/licenses', icon: KeyIcon },
    { text: 'Settings', href: '/settings', icon: Cog6ToothIcon },
]

const ROLE_LABELS: Record<string, string> = {
    admin: 'Administrator',
    coordinator: 'Coordinator',
    viewer: 'Read-Only Admin',
}

function initials(name?: string | null, email?: string | null): string {
    const source = (name || email || '?').trim()
    const parts = source.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return source.slice(0, 2).toUpperCase()
}

export default function ProviderLayout() {
    const session = useProviderSession()
    const location = useLocation()
    // Count of roadmaps awaiting review, shown as a badge on the nav. Refetched on
    // navigation so it clears as soon as the coordinator works through the queue.
    const [reviewCount, setReviewCount] = useState(0)

    useEffect(() => {
        let cancelled = false
        api<{ items: unknown[] }>(ROUTES.provider.roadmapReviews).then((res) => {
            if (!cancelled) setReviewCount(res.data?.items?.length ?? 0)
        })
        return () => {
            cancelled = true
        }
    }, [location.pathname])

    // Server-side logout: revokes the refresh token, clears the cookie, and
    // redirects back to the portal login.
    function handleSignOut() {
        window.location.href = `${import.meta.env.VITE_API_URL}/${ROUTES.auth.logout}?portal=provider`
    }

    return (
        <div className='min-h-screen bg-neutral-50'>
            <div className='flex'>
                {/* Sidebar */}
                <aside className='hidden lg:flex flex-col w-64 min-h-screen bg-brand-600 sticky top-0'>
                    <div className='px-6 py-6 border-b border-white/10'>
                        <span className='block text-xs uppercase tracking-wider text-white/60 whitespace-nowrap mb-1'>
                            BrightPath Provider
                        </span>
                        <span className='block text-sm font-semibold text-white break-words leading-snug'>
                            {session?.organization.displayName}
                        </span>
                    </div>
                    <nav className='flex-1 px-4 py-6'>
                        <ul className='space-y-1'>
                            {PROVIDER_NAV.filter((link) => !link.adminOnly || session?.role === 'admin').map((link) => (
                                <li key={link.href}>
                                    <NavLink
                                        to={link.href}
                                        end
                                        className={({ isActive }) =>
                                            cn(
                                                'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                                                isActive
                                                    ? 'bg-white/15 text-white'
                                                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                                            )
                                        }
                                    >
                                        <link.icon className='w-5 h-5 mr-3' />
                                        <span>{link.text}</span>
                                        {link.href === '/roadmap-reviews' && reviewCount > 0 && (
                                            <span className='ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold text-white bg-red-500 rounded-full'>
                                                {reviewCount}
                                            </span>
                                        )}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                <div className='flex-1 min-h-screen flex flex-col'>
                    {/* Top bar with the user menu, mirroring the family app */}
                    <header className='bg-white border-b border-neutral-200 px-4 sm:px-6'>
                        <div className='flex items-center justify-between h-14'>
                            <img src={logo} alt='BrightPath' className='h-11 w-auto' />
                            <Menu as='div' className='relative'>
                                <Menu.Button className='flex items-center gap-2 p-1.5 rounded-lg hover:bg-neutral-50'>
                                    <span className='sr-only'>Open user menu</span>
                                    <span className='w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-semibold flex items-center justify-center'>
                                        {initials(session?.user.name, session?.user.email)}
                                    </span>
                                    <span className='hidden sm:flex flex-col items-start leading-tight'>
                                        <span className='text-sm font-medium text-neutral-900'>
                                            {session?.user.name || session?.user.email}
                                        </span>
                                        <span className='text-xs text-neutral-500'>
                                            {ROLE_LABELS[session?.role ?? ''] ?? ''}
                                        </span>
                                    </span>
                                    <ChevronDownIcon className='w-4 h-4 text-neutral-400' aria-hidden='true' />
                                </Menu.Button>
                                <Transition
                                    enter='transition ease-out duration-100'
                                    enterFrom='transform opacity-0 scale-95'
                                    enterTo='transform opacity-100 scale-100'
                                    leave='transition ease-in duration-75'
                                    leaveFrom='transform opacity-100 scale-100'
                                    leaveTo='transform opacity-0 scale-95'
                                >
                                    <Menu.Items className='absolute right-0 z-20 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-gray-900/5 focus:outline-none py-1'>
                                        <div className='px-3 py-2 border-b border-neutral-100'>
                                            <div className='text-sm font-medium text-neutral-900 truncate'>
                                                {session?.user.name || session?.user.email}
                                            </div>
                                            <div className='text-xs text-neutral-500 truncate'>{session?.user.email}</div>
                                            <div className='text-xs text-neutral-500'>{ROLE_LABELS[session?.role ?? ''] ?? ''}</div>
                                        </div>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    onClick={handleSignOut}
                                                    className={cn(
                                                        'w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 text-left',
                                                        active ? 'bg-neutral-50' : ''
                                                    )}
                                                >
                                                    <ArrowRightOnRectangleIcon className='w-4 h-4' />
                                                    Sign out
                                                </button>
                                            )}
                                        </Menu.Item>
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </div>
                    </header>

                    <main className='flex-1'>
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    )
}
